import { useContext, useEffect, useRef } from 'react';
import { RealtimeEventOfType, RealtimeEventType } from 'fym-shared/src/music24/realtimeEvents';
import { RealtimeContext } from './RealtimeContext';

/**
 * Calls `handler` for each realtime event of `type`, while the component is mounted.
 *
 * Most screens do not need it: the provider already invalidates the cache tags of each event
 * (`realtimeEventTags`). Use it for a reaction that is not a refetch, for example a toast. The
 * event holds ids only; read the data through the API.
 */
export function useRealtimeEvent<T extends RealtimeEventType>(
  type: T,
  handler: (event: RealtimeEventOfType<T>) => void
): void {
  const { subscribe } = useContext(RealtimeContext);
  // The newest handler, so a handler made on each render does not subscribe again.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(
    () =>
      subscribe((event) => {
        if (event.type === type) {
          handlerRef.current(event as RealtimeEventOfType<T>);
        }
      }),
    [subscribe, type]
  );
}
